import React, { useState, useRef, useCallback } from 'react';
import { useAppStore, ALL_PROPS, KeyframeProp, LayerKeyframes, LayerEasings, emptyLayerKeyframes } from '../store/useAppStore';
import { viewportActions } from '../viewportActions';
import { SNAP_PX } from '../components/timeline/timelineConstants';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface LayerBarDragState {
  layerId: string;
  draggedLayerIds: string[]; // All layers being moved (includes layerId and other selected layers)
  mode: 'move' | 'left' | 'right';
  startFrameStart: number;
  startFrameEnd: number;
  currentDelta: number;
}

interface DragInternal extends LayerBarDragState {
  startClientX: number;
  startPositions: Record<string, { frameStart: number; frameEnd: number }>; // For all dragged layers
}

interface Options {
  pxPerFrame: number;
  totalFrames: number;
}

// ── Hook ──────────────────────────────────────────────────────────────────────

export function useLayerBarDrag({ pxPerFrame, totalFrames }: Options) {
  const [barDragState, setBarDragState] = useState<LayerBarDragState | null>(null);
  const barDragRef    = useRef<DragInternal | null>(null);
  const dragJustCompletedRef = useRef(false);
  const pxPerFrameRef = useRef(pxPerFrame);
  pxPerFrameRef.current = pxPerFrame;
  const totalFramesRef = useRef(totalFrames);
  totalFramesRef.current = totalFrames;

  const handleBarPointerDown = useCallback(
    (
      e: React.PointerEvent,
      layerId: string,
      mode: 'move' | 'left' | 'right',
      frameStart: number,
      frameEnd: number,
      selectedLayerIds: string[] = [layerId],
    ) => {
      e.stopPropagation();
      e.preventDefault();
      (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
      
      // Determine which layers to drag
      const draggedLayerIds = selectedLayerIds.includes(layerId) ? selectedLayerIds : [layerId];
      
      // Store initial positions of all dragged layers
      const { layers } = useAppStore.getState();
      const startPositions: Record<string, { frameStart: number; frameEnd: number }> = {};
      for (const id of draggedLayerIds) {
        const layer = layers.find((l) => l.id === id);
        if (layer) {
          startPositions[id] = { frameStart: layer.frameStart ?? 0, frameEnd: layer.frameEnd ?? (totalFramesRef.current - 1) };
        }
      }
      
      const drag: DragInternal = {
        layerId, draggedLayerIds, mode,
        startFrameStart: frameStart, startFrameEnd: frameEnd,
        currentDelta: 0, startClientX: e.clientX,
        startPositions,
      };
      barDragRef.current = drag;
      setBarDragState({ layerId, draggedLayerIds, mode, startFrameStart: frameStart, startFrameEnd: frameEnd, currentDelta: 0 });
    },
    [],
  );

  const handleBarPointerMove = useCallback((e: React.PointerEvent) => {
    const drag = barDragRef.current;
    if (!drag) return;
    const px = pxPerFrameRef.current;
    if (!px) return;
    const rawDeltaPx = e.clientX - drag.startClientX;
    let delta = Math.round(rawDeltaPx / px);
    if (e.shiftKey) {
      const cf = Math.round(useAppStore.getState().currentFrame);
      const totalF = totalFramesRef.current;
      if (drag.mode === 'move') {
        // Check both edges; snap whichever is closer to a marker.
        const startEdgePx = drag.startFrameStart * px + rawDeltaPx;
        const endEdgePx   = drag.startFrameEnd   * px + rawDeltaPx;
        let bestDist = Infinity;
        let snapDelta = delta;
        for (const f of [0, totalF - 1, cf]) {
          const distStart = Math.abs(startEdgePx - f * px);
          const distEnd   = Math.abs(endEdgePx   - f * px);
          if (distStart <= SNAP_PX && distStart < bestDist) {
            bestDist  = distStart;
            snapDelta = f - drag.startFrameStart;
          }
          if (distEnd <= SNAP_PX && distEnd < bestDist) {
            bestDist  = distEnd;
            snapDelta = f - drag.startFrameEnd;
          }
        }
        delta = snapDelta;
      } else {
        // left/right trim: single edge
        const edgePx = drag.mode === 'right'
          ? drag.startFrameEnd   * px + rawDeltaPx
          : drag.startFrameStart * px + rawDeltaPx;
        for (const f of [0, totalF - 1, cf]) {
          if (Math.abs(edgePx - f * px) <= SNAP_PX) {
            delta = drag.mode === 'right' ? f - drag.startFrameEnd : f - drag.startFrameStart;
            break;
          }
        }
      }
    }
    if (delta !== drag.currentDelta) {
      drag.currentDelta = delta;
      setBarDragState({ layerId: drag.layerId, draggedLayerIds: drag.draggedLayerIds, mode: drag.mode, startFrameStart: drag.startFrameStart, startFrameEnd: drag.startFrameEnd, currentDelta: delta });
    }
  }, []);

  const handleBarPointerUp = useCallback(
    (
      e: React.PointerEvent,
      layerId: string,
      mode: 'move' | 'left' | 'right',
    ) => {
      const drag = barDragRef.current;
      if (!drag || drag.layerId !== layerId || drag.mode !== mode) return;
      barDragRef.current = null;
      setBarDragState(null);

      const delta = drag.currentDelta;
      if (delta === 0) return;
      dragJustCompletedRef.current = true;
      // Clear the flag after a short delay to not interfere with subsequent interactions
      setTimeout(() => { dragJustCompletedRef.current = false; }, 50);

      const totalF = totalFramesRef.current;
      const origStart = drag.startFrameStart;
      const origEnd   = drag.startFrameEnd;

      viewportActions.pushSnapshot.current();

      if (mode === 'move') {
        // Move all dragged layers together
        useAppStore.setState((s) => {
          const updatedLayers = s.layers.map((layer) => {
            if (!drag.draggedLayerIds.includes(layer.id)) return layer;
            
            const startPos = drag.startPositions[layer.id];
            if (!startPos) return layer;
            
            // Calculate intended new positions by applying delta to both
            let newStart = startPos.frameStart + delta;
            let newEnd = startPos.frameEnd + delta;
            
            // Smart boundary handling: when one edge hits a boundary, only that edge locks
            // The other edge continues to move, allowing the bar to shorten/lengthen asymmetrically
            
            // Handle start boundary
            if (newStart < 0) {
              newStart = 0;
            } else if (newStart > totalF - 1) {
              newStart = totalF - 1;
            }
            
            // Handle end boundary
            if (newEnd < 0) {
              newEnd = 0;
            } else if (newEnd > totalF - 1) {
              newEnd = totalF - 1;
            }
            
            // Ensure bar is at least 1 frame long
            if (newEnd <= newStart) {
              newEnd = newStart + 1;
              if (newEnd > totalF - 1) {
                newEnd = totalF - 1;
                newStart = newEnd - 1;
              }
            }
            
            return { ...layer, frameStart: newStart, frameEnd: newEnd };
          });
          
          // Shift keyframes and easing for all dragged layers
          const newLayerKeyframes = { ...s.layerKeyframes };
          const newKeyframeEasings = { ...s.keyframeEasings };
          
          for (const dLayerId of drag.draggedLayerIds) {
            const layerKfs = s.layerKeyframes[dLayerId];
            const newKfsForLayer: LayerKeyframes = emptyLayerKeyframes();
            if (layerKfs) {
              for (const prop of ALL_PROPS) {
                const src = layerKfs[prop] as Record<number, number>;
                for (const [f, v] of Object.entries(src)) {
                  const newF = Math.max(0, Math.min(totalF - 1, Number(f) + delta));
                  newKfsForLayer[prop][newF] = v;
                }
              }
            }
            
            // Shift easing data too.
            const oldEasings = s.keyframeEasings[dLayerId] ?? {};
            const newEasingsForLayer: LayerEasings = {};
            for (const prop of ALL_PROPS as KeyframeProp[]) {
              const propEas = oldEasings[prop];
              if (propEas) {
                const shifted: typeof propEas = {};
                for (const [f, eas] of Object.entries(propEas)) {
                  const newF = Math.max(0, Math.min(totalF - 1, Number(f) + delta));
                  shifted[newF] = eas;
                }
                newEasingsForLayer[prop] = shifted;
              }
            }
            
            newLayerKeyframes[dLayerId] = newKfsForLayer;
            newKeyframeEasings[dLayerId] = newEasingsForLayer;
          }
          
          return {
            layers: updatedLayers,
            layerKeyframes: newLayerKeyframes,
            keyframeEasings: newKeyframeEasings,
          };
        });
      } else if (mode === 'left') {
        // Only trim the primary dragged layer
        const newStart = Math.max(0, Math.min(origEnd - 1, origStart + delta));
        useAppStore.setState((s) => ({
          layers: s.layers.map((l) => l.id === layerId ? { ...l, frameStart: newStart } : l),
        }));
      } else {
        // Only trim the primary dragged layer
        const newEnd = Math.max(origStart + 1, Math.min(totalF - 1, origEnd + delta));
        useAppStore.setState((s) => ({
          layers: s.layers.map((l) => l.id === layerId ? { ...l, frameEnd: newEnd } : l),
        }));
      }
    },
    [],
  );

  return { barDragState, dragJustCompletedRef, handleBarPointerDown, handleBarPointerMove, handleBarPointerUp };
}
